import { useState, useEffect } from "react";
import { map } from "lodash";
import swal from "sweetalert2";
import { useHooksSubsidiary, useHooksUser, useHooksGang } from "../../../hooks";
import { getRolesApi } from "../../../apis/apis.hrm/apisRoles";

const initialState = {
    id: 0, first_name: "", last_name: "", document: "", phone: "",
    code: "0", email: "", password: "", is_superuser: false,
    is_staff: false, is_active: false, subsidiary_id: 0, subsidiary_name: "",
    birth_date: "", relative_phone: "", start_date: "", end_date: "",
    gang_id: 0, gang_name: "", address: "", role_id: 0, role_name: "",
    assigned_gangs: [],
};

// ── shared input styles ──────────────────────────────────────────────────────
const inputCls = "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const labelCls = "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1";

function Fieldset({ icon, title, children }) {
    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <i className={`fas ${icon} text-blue-500`}></i>
                    {title}
                </h6>
            </div>
            <div className="px-4 py-4">{children}</div>
        </div>
    );
}

export function FragmentAddEditUser({ onRefetch, user, onClose }) {
    const [roles, setRoles] = useState([]);
    const { addUser, updateUser } = useHooksUser();
    const [itemUser, setItemUser] = useState(initialState);
    const { subsidiarys, getSubsidiarys } = useHooksSubsidiary();
    const { gangs, getGangs } = useHooksGang();

    const loadRoles = async () => { setRoles(await getRolesApi()); };

    useEffect(() => { getSubsidiarys(); getGangs(); loadRoles(); }, []);

    useEffect(() => {
        setItemUser(
            !user.id ? initialState : { ...initialState, ...user, assigned_gangs: user.assigned_gangs || [] }
        );
    }, [user]);

    const handleChange = (e) => setItemUser({ ...itemUser, [e.target.name]: e.target.value });
    const handleChangeCheckbox = (e) => setItemUser({ ...itemUser, [e.target.name]: e.target.checked });

    const handleChangeAssignedGangs = (gangId) => {
        const current = itemUser.assigned_gangs || [];
        const isSelected = current.some((g) => g.id === gangId);
        let newGangs;
        if (isSelected) {
            newGangs = current.filter((g) => g.id !== gangId);
        } else {
            const gangToAdd = gangs.find((g) => g.gangID === gangId);
            newGangs = gangToAdd
                ? [...current, { id: gangToAdd.gangID, name: gangToAdd.gangName, warehouse_id: gangToAdd.warehouse_id, warehouse_name: gangToAdd.warehouse_name }]
                : current;
        }
        setItemUser({ ...itemUser, assigned_gangs: newGangs });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const checks = [
            [!e.target.document.value,   "Ingrese un número de documento"],
            [!e.target.first_name.value, "Ingrese el nombre del empleado"],
            [!e.target.last_name.value,  "Ingrese los apellidos del empleado"],
            [!e.target.email.value,      "Ingrese el correo electrónico"],
            [!e.target.address.value,    "Ingrese la dirección del empleado"],
            [!e.target.role_id.value || e.target.role_id.value === "0", "Seleccione un rol"],
            [!user.id && !e.target.password.value, "Ingrese la contraseña de usuario"],
        ];
        for (const [cond, msg] of checks) {
            if (cond) { swal.fire({ icon: "error", title: "Atención", text: msg }); return; }
        }
        try {
            const payload = { ...itemUser, assigned_gangs: itemUser.assigned_gangs?.map((g) => g.id) || [] };
            if (!user.id) {
                await addUser(payload);
                swal.fire({ icon: "success", title: "Empleado registrado", timer: 1500, showConfirmButton: false });
            } else {
                await updateUser(payload);
                swal.fire({ icon: "success", title: "Empleado actualizado", timer: 1500, showConfirmButton: false });
            }
            onClose?.();
            onRefetch();
        } catch (error) {
            swal.fire({ icon: "error", title: "Error en la operación", text: String(error) });
        }
    };

    const selectedRole = roles.find((r) => r.id === parseInt(itemUser.role_id));
    const isRepartidor = selectedRole?.name?.includes("REPARTIDOR");

    return (
        <form id="form-person" onSubmit={handleSubmit} className="px-6 py-5 space-y-4">

            {/* Información Organizacional */}
            <Fieldset icon="fa-building" title="Información Organizacional">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <label className={labelCls}><i className="fas fa-building mr-1"></i>Sucursal</label>
                        <select name="subsidiary_id" value={itemUser.subsidiary_id} onChange={handleChange} className={inputCls} required>
                            <option value={0}>Seleccione una sucursal</option>
                            {map(subsidiarys, (item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}><i className="fas fa-users mr-1"></i>Cuadrilla</label>
                        <select name="gang_id" value={itemUser.gang_id} onChange={handleChange} className={inputCls}>
                            <option value={0}>Sin cuadrilla</option>
                            {map(gangs, (item) => <option key={item.gangID} value={item.gangID}>{item.gangName}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}><i className="fas fa-user-tie mr-1"></i>Rol</label>
                        <select name="role_id" value={itemUser.role_id} onChange={handleChange} className={inputCls}>
                            <option value={0}>Sin rol</option>
                            {roles.map((r, i) => <option key={r.id || i} value={r.id}>{r.name}</option>)}
                        </select>
                    </div>
                </div>

                {/* Cuadrillas asignadas — solo para repartidores */}
                {isRepartidor && (
                    <div className="mt-4 rounded-xl border border-cyan-200 dark:border-cyan-700 overflow-hidden">
                        <div className="px-4 py-3 bg-cyan-50 dark:bg-cyan-900/20 border-b border-cyan-200 dark:border-cyan-700">
                            <p className="text-sm font-semibold text-cyan-700 dark:text-cyan-300 flex items-center gap-2">
                                <i className="fas fa-route"></i>
                                Cuadrillas asignadas
                            </p>
                            <p className="text-xs text-cyan-600 dark:text-cyan-400 mt-0.5">
                                Seleccione las cuadrillas que puede atender este repartidor
                            </p>
                        </div>
                        <div className="px-4 py-3">
                            {gangs && gangs.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {gangs.map((gang) => {
                                        const isChecked = itemUser.assigned_gangs?.some((ag) => ag.id === gang.gangID) || false;
                                        return (
                                            <label
                                                key={gang.gangID}
                                                className={`inline-flex items-center gap-1.5 cursor-pointer rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                                                    isChecked
                                                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                                                        : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-400"
                                                }`}
                                            >
                                                <input type="checkbox" className="sr-only" checked={isChecked} onChange={() => handleChangeAssignedGangs(gang.gangID)} />
                                                <i className="fas fa-users text-[10px]"></i>
                                                {gang.gangName}
                                            </label>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-xs text-amber-600 dark:text-amber-400">
                                    <i className="fas fa-spinner fa-spin mr-1"></i>Cargando cuadrillas...
                                </p>
                            )}
                            {itemUser.assigned_gangs?.length > 0 && (
                                <p className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                                    <i className="fas fa-info-circle mr-1"></i>
                                    {itemUser.assigned_gangs.length} cuadrilla(s) seleccionada(s)
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </Fieldset>

            {/* Información Personal */}
            <Fieldset icon="fa-user" title="Información Personal">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                    <div className="sm:col-span-2">
                        <label className={labelCls}><i className="fas fa-hashtag mr-1"></i>Código</label>
                        <input type="text" name="code" value={itemUser.code || ""} onChange={handleChange} maxLength={10} placeholder="Código" className={inputCls} />
                    </div>
                    <div className="sm:col-span-5">
                        <label className={labelCls}><i className="fas fa-user mr-1"></i>Nombres *</label>
                        <input type="text" name="first_name" value={itemUser.first_name || ""} onChange={handleChange} maxLength={150} placeholder="Ingrese los nombres" autoComplete="given-name" required className={inputCls} />
                    </div>
                    <div className="sm:col-span-5">
                        <label className={labelCls}><i className="fas fa-user mr-1"></i>Apellidos *</label>
                        <input type="text" name="last_name" value={itemUser.last_name || ""} onChange={handleChange} maxLength={150} placeholder="Ingrese los apellidos" autoComplete="family-name" required className={inputCls} />
                    </div>
                </div>
            </Fieldset>

            {/* Información de Contacto */}
            <Fieldset icon="fa-address-book" title="Información de Contacto">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <label className={labelCls}><i className="fas fa-id-card mr-1"></i>Nº Documento *</label>
                        <input type="text" name="document" value={itemUser.document || ""} onChange={handleChange} maxLength={15} placeholder="Número de documento" autoComplete="off" required className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}><i className="fas fa-phone mr-1"></i>Teléfono / Celular</label>
                        <input type="tel" name="phone" value={itemUser.phone || ""} onChange={handleChange} maxLength={12} placeholder="Teléfono o celular" autoComplete="tel" className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}><i className="fas fa-home mr-1"></i>Dirección</label>
                        <input type="text" name="address" value={itemUser.address || ""} onChange={handleChange} maxLength={500} placeholder="Dirección de residencia" autoComplete="address-line1" className={inputCls} />
                    </div>
                </div>
            </Fieldset>

            {/* Configuración de Usuario */}
            <Fieldset icon="fa-cog" title="Configuración de Usuario">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                    {/* Permisos y Estado */}
                    <div className="space-y-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            <i className="fas fa-shield-alt mr-1 text-amber-500"></i>Permisos y Estado
                        </p>

                        {/* Super usuario toggle */}
                        <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
                            <div>
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                    <i className={`fas ${itemUser.is_superuser ? "fa-crown text-amber-500" : "fa-user text-gray-400"} mr-2`}></i>
                                    Super Usuario
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {itemUser.is_superuser ? "Acceso completo al sistema" : "Acceso limitado según su rol"}
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={itemUser.is_superuser}
                                onClick={() => setItemUser({ ...itemUser, is_superuser: !itemUser.is_superuser })}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${itemUser.is_superuser ? "bg-blue-600" : "bg-gray-200 dark:bg-gray-600"}`}
                            >
                                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${itemUser.is_superuser ? "translate-x-5" : "translate-x-0"}`} />
                            </button>
                        </div>

                        {/* Estado activo toggle */}
                        <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
                            <div>
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                    <i className={`fas ${itemUser.is_active ? "fa-check-circle text-emerald-500" : "fa-times-circle text-red-500"} mr-2`}></i>
                                    Estado del Usuario
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {itemUser.is_active ? "Puede acceder al sistema" : "No puede acceder al sistema"}
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={itemUser.is_active}
                                onClick={() => setItemUser({ ...itemUser, is_active: !itemUser.is_active })}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${itemUser.is_active ? "bg-emerald-600" : "bg-gray-200 dark:bg-gray-600"}`}
                            >
                                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${itemUser.is_active ? "translate-x-5" : "translate-x-0"}`} />
                            </button>
                        </div>
                    </div>

                    {/* Credenciales */}
                    <div className="space-y-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            <i className="fas fa-key mr-1 text-blue-500"></i>Credenciales de Acceso
                        </p>
                        <div>
                            <label className={labelCls}><i className="fas fa-envelope mr-1"></i>Correo Electrónico *</label>
                            <input type="email" name="email" value={itemUser.email || ""} onChange={handleChange} maxLength={200} placeholder="correo@ejemplo.com" autoComplete="email" required className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>
                                <i className="fas fa-lock mr-1"></i>Contraseña {!user.id && "*"}
                            </label>
                            <input type="password" name="password" value={itemUser.password || ""} onChange={handleChange} maxLength={20} placeholder="Contraseña de acceso" autoComplete="new-password" required={!user.id} className={inputCls} />
                            {user.id && (
                                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                                    Dejar vacío para mantener la contraseña actual
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </Fieldset>

            {/* Información Adicional */}
            <Fieldset icon="fa-calendar-alt" title="Información Adicional">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}><i className="fas fa-birthday-cake mr-1"></i>Fecha de Nacimiento</label>
                        <input type="date" name="birth_date" value={itemUser.birth_date || ""} onChange={handleChange} className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}><i className="fas fa-phone mr-1"></i>Teléfono de Pariente</label>
                        <input type="tel" name="relative_phone" value={itemUser.relative_phone || ""} onChange={handleChange} maxLength={9} placeholder="Teléfono de contacto" className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}><i className="fas fa-calendar-check mr-1"></i>Fecha de Inicio</label>
                        <input type="date" name="start_date" value={itemUser.start_date || ""} onChange={handleChange} className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}><i className="fas fa-calendar-times mr-1"></i>Fecha de Finalización</label>
                        <input type="date" name="end_date" value={itemUser.end_date || ""} onChange={handleChange} className={inputCls} />
                    </div>
                </div>
            </Fieldset>

            {/* Submit */}
            <div className="flex justify-center pt-2">
                <button
                    type="submit"
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold px-8 py-2.5 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                >
                    <i className={`fas ${user.id ? "fa-save" : "fa-user-plus"}`}></i>
                    {user.id ? "Actualizar Empleado" : "Registrar Empleado"}
                </button>
            </div>
        </form>
    );
}
